import { useCallback, useEffect, useMemo, useState } from "react";
import { FileSpreadsheet, FileText, RefreshCw, Scissors } from "lucide-react";
import stitchingEntryApi from "../api/stitchingEntryApi";
import usePerm from "../hooks/usePerm";
import { downloadBlob, blobErrorMessage } from "../utils/downloadBlob";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Alert from "../components/Alert";
import Select2 from "../components/Select2";
import SearchBar from "../components/SearchBar";
import WorkLocationSelect from "../components/WorkLocationSelect";
import T from "../theme/colors";

const fmtAmt = n => "₹" + (Number(n) || 0).toFixed(2);
const fmtDate = d => d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN",
  { day: "2-digit", month: "short", year: "numeric" }) : "—";
// Local dates, not toISOString() (UTC): before 5:30 AM IST the UTC date is yesterday.
const iso = d => {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 10);
};
const monthStart = () => { const d = new Date(); d.setDate(1); return iso(d); };

/**
 * STITCHING REPORT — every line of the confirmed stitching entries in a
 * period: date, employee, barcode, operation, qty, rate per piece and amount,
 * with the total and each employee's total (what they are owed for the
 * period). Narrow it by employee, operation, or barcode / product / SO.
 * Excel and PDF carry the same rows.
 */
export default function StitchingReportPage() {
  const can = usePerm("STITCHING_ENTRY");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(iso(new Date()));
  const [employeeId, setEmployeeId] = useState("");
  const [operationId, setOperationId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [q, setQ] = useState("");
  const [employees, setEmployees] = useState([]);
  const [operations, setOperations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    stitchingEntryApi.employees().then(r => setEmployees(r.data?.data || r.data || [])).catch(() => setEmployees([]));
    stitchingEntryApi.operations().then(r => setOperations(r.data?.data || r.data || [])).catch(() => setOperations([]));
  }, []);

  const params = useMemo(() => ({
    from: from || undefined, to: to || undefined,
    employeeId: employeeId || undefined, operationId: operationId || undefined,
    locationId: locationId || undefined,
    q: q.trim() || undefined,
  }), [from, to, employeeId, operationId, locationId, q]);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const res = await stitchingEntryApi.report(params);
      setData(res.data?.data ?? res.data);
    } catch (e) {
      setErr(e?.response?.data?.message || "Could not load the report");
      setData(null);
    }
    setLoading(false);
  }, [params]);

  // Reload as the filters change; typing in search waits a moment.
  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
  }, [load]);

  const exportFile = async kind => {
    setBusy(kind); setErr("");
    try {
      if (kind === "excel") {
        downloadBlob(await stitchingEntryApi.reportExcel(params), `stitching-report-${from}-to-${to}.xlsx`);
      } else {
        const res = await stitchingEntryApi.reportPdf(params);
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (e) {
      setErr(await blobErrorMessage(e, kind === "excel" ? "Excel export failed" : "PDF failed"));
    }
    setBusy("");
  };

  const rows = useMemo(() => data?.rows || [], [data]);
  const byEmployee = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      const k = r.employeeName || "—";
      const t = m.get(k) || { name: k, code: r.employeeCode, qty: 0, amount: 0 };
      t.qty += Number(r.qty) || 0;
      t.amount += Number(r.amount) || 0;
      m.set(k, t);
    });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const empOptions = [{ value: "", label: "All employees" },
    ...employees.map(e => ({ value: String(e.id), label: `${e.employeeName}${e.employeeCode ? ` (${e.employeeCode})` : ""}` }))];
  const opOptions = [{ value: "", label: "All operations" },
    ...operations.map(o => ({ value: String(o.operationId), label: o.operationName }))];

  return (
    <div className="fade-up" style={{ padding: "20px 24px" }}>
      <PageHeader
        title="Stitching Report"
        subtitle="Every stitching line in the period — date, employee, barcode, operation, qty, rate per piece and amount"
        breadcrumb={["Production", "Stitching Report"]}
        actions={<>
          <Btn variant="ghost" size="sm" icon={<Scissors size={13} />}
            onClick={() => { window.location.hash = "stitching-entry"; }}>Stitching Entry</Btn>
          {can.export && <Btn variant="outline" size="sm" icon={<FileSpreadsheet size={13} />}
            loading={busy === "excel"} disabled={!rows.length} onClick={() => exportFile("excel")}>Excel</Btn>}
          {can.print && <Btn variant="outline" size="sm" icon={<FileText size={13} />}
            loading={busy === "pdf"} disabled={!rows.length} onClick={() => exportFile("pdf")}>PDF</Btn>}
        </>}
      />

      <Alert message={err} onClose={() => setErr("")} />

      <Card>
        <div style={{
          padding: "12px 16px", borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
        }}>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} title="From" style={dateInput} />
          <span style={{ color: "var(--text-2)", fontSize: 12 }}>to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} title="To" style={dateInput} />
          <WorkLocationSelect mode="filter" value={locationId} onChange={setLocationId} />
          <div style={{ minWidth: 220 }}>
            <Select2 value={employeeId} onChange={v => setEmployeeId(v || "")} options={empOptions} placeholder="All employees" />
          </div>
          <div style={{ minWidth: 200 }}>
            <Select2 value={operationId} onChange={v => setOperationId(v || "")} options={opOptions} placeholder="All operations" />
          </div>
          <SearchBar value={q} onChange={setQ} placeholder="Barcode, product or SO…" />
          <Btn variant="ghost" size="sm" icon={<RefreshCw size={13} />} onClick={load}>Refresh</Btn>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--bg-input)" }}>
                <th style={th}>Date</th>
                <th style={th}>Employee</th>
                <th style={th}>Barcode</th>
                <th style={th}>Operation</th>
                <th style={{ ...th, textAlign: "right" }}>Qty</th>
                <th style={{ ...th, textAlign: "right" }}>Rate / Pc</th>
                <th style={{ ...th, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading && !rows.length && (
                <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "var(--text-2)", padding: 30 }}>Loading…</td></tr>
              )}
              {!loading && !rows.length && (
                <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "var(--text-2)", padding: 30 }}>
                  No stitching work in this period{employeeId || operationId || locationId || q ? " for these filters" : ""}.
                </td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${T.border}` }}>
                  <td style={{ ...td, whiteSpace: "nowrap" }} title={r.entryNumber}>{fmtDate(r.entryDate)}</td>
                  <td style={td}>
                    <span style={{ fontWeight: 600 }}>{r.employeeName || "—"}</span>
                    {r.employeeCode && <span style={{ marginLeft: 6, fontSize: 11.5, color: "var(--text-2)" }}>{r.employeeCode}</span>}
                  </td>
                  <td style={td}>
                    <div style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--primary)" }}>{r.barcode}</div>
                    {r.productName && <div style={{ fontSize: 11.5, color: "var(--text-2)" }}>{r.productName}</div>}
                  </td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.operationName}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{r.qty}</td>
                  <td style={{ ...td, textAlign: "right" }}>{fmtAmt(r.ratePerPiece)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmtAmt(r.amount)}</td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr style={{ borderTop: `2px solid ${T.border}`, background: "var(--bg-input)" }}>
                  <td style={{ ...td, fontWeight: 800 }} colSpan={4}>Total · {rows.length} line{rows.length === 1 ? "" : "s"}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 800 }}>{data.totalQty}</td>
                  <td style={td} />
                  <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "var(--success)" }}>{fmtAmt(data.totalAmount)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {data?.truncated && (
          <div style={{ padding: "8px 16px", fontSize: 12.5, color: "var(--warn)" }}>
            Only the first {rows.length} lines are listed — narrow the dates or filters.
          </div>
        )}
      </Card>

      {byEmployee.length > 1 && (
        <Card style={{ marginTop: 14 }}>
          <div style={{ padding: "10px 16px", fontWeight: 700, color: "var(--text-0)", borderBottom: `1px solid ${T.border}` }}>
            Employee totals
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--bg-input)" }}>
                <th style={th}>Employee</th>
                <th style={{ ...th, textAlign: "right" }}>Qty</th>
                <th style={{ ...th, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {byEmployee.map(e => (
                <tr key={e.name} style={{ borderTop: `1px solid ${T.border}` }}>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {e.name}{e.code && <span style={{ marginLeft: 6, fontSize: 11.5, color: "var(--text-2)" }}>{e.code}</span>}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{e.qty}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmtAmt(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// CSS variables, so both themes read correctly.
const th = {
  textAlign: "left", padding: "8px 14px", fontSize: 11.5, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-2)",
  borderBottom: "1px solid var(--border)",
};
const td = { padding: "8px 14px", color: "var(--text-0)", verticalAlign: "middle" };
const dateInput = {
  padding: "7px 10px", borderRadius: 7, fontSize: 13, outline: "none",
  border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-0)",
};
